export type StaticLegalDoc = {
  type: 'terms' | 'privacy'
  title: string
  content: string
  version: number
  last_updated: string
}

export const TERMS_VERSION = 1
export const PRIVACY_VERSION = 1
export const TERMS_LAST_UPDATED = 'January 22, 2026'
export const PRIVACY_LAST_UPDATED = 'January 22, 2026'

// NOTE: These are static, client-bundled copies to avoid DB/API dependency.
// They mirror the content shown on the public pages so the modal matches.
export const TERMS_MARKDOWN: string = `# Terms and Conditions

This website is operated by Travel Warehouse Inc. ("we", "us", or "our"). Access to and use of the website is subject to these terms and our Privacy Policy.

## Section 1. Our terms of use
### 1.1 Read And Understand
Please ensure you have read and understood these Terms and our Privacy Policy. We recommend saving/printing a copy for reference.
### 1.2 Changes To Terms
We may vary our terms of use from time to time. The revised Terms will be available on our website. Continued use constitutes acceptance.

## Section 2. Accuracy and availability of our website
- We strive to keep the website accurate and up-to-date, but you are responsible for your own Internet security.
- The website is provided free of charge and may not meet your specific requirements.
- Materials are for general information only and are not advice.
- Access may be suspended or terminated without notice.

## Section 3. Ownership
- This website and its content compilation are our copyrighted property.
- Trademarks and logos are our registered marks; no license is granted without prior written permission.

## Section 4. Access to our website
- You are responsible for arrangements and costs to access the website.
- Ensure all persons using your connection are aware of these Terms.
### 4.3 Rules Of Use
- Do not use automated systems to extract content without permission.
- Do not use the website for illegal purposes or to transmit malware.
- Do not interfere with rendering, networks, or inject code.
- Do not imply an association with us without permission.
- Do not copy, reproduce, or distribute content without permission (personal, non‑commercial use allowed).
### 4.4–4.7 Additional Terms
- Access can be terminated or restricted without notice.
- Content you submit may be used by us as permitted by law.
- We may monitor transmissions and remove inappropriate content.
- Suggestions or ideas submitted grant us a non‑exclusive, royalty‑free, perpetual, irrevocable right to use them.

## Section 5. Your privacy and cookies
- Personal information is handled in accordance with our Privacy Policy.
- We use cookies and similar technologies as described therein.

## Section 6. Our intellectual property rights
- All rights in the website and its contents are reserved.
- Use of any trademark requires our prior written permission.

## Section 7. Our liabilities to each other
- Nothing here affects your statutory rights.
- We are not liable for losses not caused by us or outside our control.

## Section 8. Third‑party websites and services
- Links are provided at your own risk and subject to third‑party terms.

## Section 9. General provisions
- You confirm information provided is complete and accurate.
- No assignment or sub‑license of rights.
- Invalid provisions do not affect the remainder.
- Philippine law applies; courts in Pasig City have exclusive jurisdiction.
`

export const PRIVACY_MARKDOWN: string = `# Privacy Policy

Travel Warehouse Inc. ("we", "us", or "our") is committed to safeguarding privacy and ensuring that the personal information of its clients ("you" or "your") is protected. This Policy explains how we collect and use personal information.

## Section 1. How we collect personal information
### 1.1 Information You Provide
- Via forms on our website, communications with us, reporting problems, or requesting support.
### 1.2 Information From Vendors And Suppliers
- We may receive personal information from third‑party vendors/suppliers to provide and improve services, tailor marketing, and prevent fraud.
### 1.3 Publicly Available Information
- Content you make public on social media when interacting with us.
### 1.4 Information From Key Partners
- Event partners and sponsors, media/digital partners, and e‑commerce platforms.

## Section 2. Personal information we collect and how we use it

| Category | Sources | Purposes |
| :-- | :-- | :-- |
| Personal and online identifiers | a. Directly from you  b. From our partners and sponsors | a. Provide and improve services  b. Personalization  c. Customer support  d. Marketing/advertising  e. Security and fraud prevention |
| Financial account information | a. Directly from you  b. From payment providers  c. From e‑commerce vendors | a. Provide services  b. Sell merchandise  c. Support commerce‑related content |
| Customer profile information | a. Directly from you  b. From our partners  c. From third‑party data providers | a. Provide and improve services  b. Personalization |
| Transactional information | a. Directly from you  b. From support partners | a. Provide and improve services  b. Support analytics  c. R&D |
| Communications with you | a. Directly from you | a. Provide and improve services |
| Online activity information | a. Directly from you  b. Cookies/technologies  c. Partners/sponsors  d. Third‑party data providers | a. Provide and improve services  b. Personalization  c. Direct marketing  d. Targeted advertising |
| Non‑precise geolocation | a. Directly from you  b. Cookies/technologies  c. Partners/sponsors  d. Tech providers | a. Provide and improve service  b. Personalization  c. Fraud and cybersecurity  d. Direct/targeted marketing |
| Sensory information | a. Directly from you  b. Tech providers | a. Provide/improve services  b. Personalization  c. Enhance experiences  d. R&D |
| Inferences | a. Third‑party data providers  b. Partners/sponsors | a. Provide/improve services  b. Personalization  c. Direct marketing  d. Analytics |

## Section 3. Disclosure of personal information
We may share data with third‑parties; some disclosures may constitute a sale under Philippine law. You may opt out where applicable.

## Section 4. Marketing and advertising
- We tailor marketing and advertising and may share with advertising networks/social platforms. Sensitive info is not shared for targeting.

## Section 5. Cookies and similar technologies
We use cookies and similar technologies to provide a good user experience and for the purposes described in this Policy.

## Section 6. Retention, disclosure, and transfer
- We keep personal information as long as necessary and use appropriate safeguards.
- Some protections do not apply to information you make public.

## Section 7. Updates
Updates will be posted on our website. If you disagree, discontinue use.

## Section 8. General provisions
- Philippine law applies; courts in Pasig City have exclusive jurisdiction.
`

export const STATIC_TERMS: StaticLegalDoc = {
  type: 'terms',
  title: 'Terms of Service',
  content: TERMS_MARKDOWN,
  version: TERMS_VERSION,
  last_updated: TERMS_LAST_UPDATED,
}

export const STATIC_PRIVACY: StaticLegalDoc = {
  type: 'privacy',
  title: 'Privacy Policy',
  content: PRIVACY_MARKDOWN,
  version: PRIVACY_VERSION,
  last_updated: PRIVACY_LAST_UPDATED,
}
